import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authenticatedFetch } from '@/utils/apiUtils';
import { getApiUrl } from '@/config/api';
import type { CalendarPayload } from '../types';

export function calendarKey(athleteId: number, fromDate: string, toDate: string) {
  return ['calendar', 'payload', athleteId, fromDate, toDate] as const;
}

export function useCalendarData(
  athleteId: number | null,
  fromDate: string,
  toDate: string,
) {
  return useQuery({
    queryKey: calendarKey(athleteId ?? 0, fromDate, toDate),
    queryFn: async (): Promise<CalendarPayload> => {
      const url = getApiUrl(
        `/api/calendar/v1/?athlete_id=${athleteId}` +
        `&from_date=${fromDate}&to_date=${toDate}`,
      );
      const r = await authenticatedFetch(url, { method: 'GET' }, 'fetching calendar');
      if (!r.success) throw new Error(r.error || 'calendar fetch failed');
      return r.data as CalendarPayload;
    },
    enabled: !!athleteId,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePrefetchCalendar() {
  const qc = useQueryClient();
  return (athleteId: number, fromDate: string, toDate: string) =>
    qc.prefetchQuery({
      queryKey: calendarKey(athleteId, fromDate, toDate),
      queryFn: async () => {
        const r = await authenticatedFetch(
          getApiUrl(`/api/calendar/v1/?athlete_id=${athleteId}&from_date=${fromDate}&to_date=${toDate}`),
          { method: 'GET' }, 'prefetching calendar',
        );
        return r.data as CalendarPayload;
      },
    });
}
